<template>
  <div class="page-container">
    <!-- 日期选择器区域 -->
    <div class="date-selector-container">
      <div class="date-selectors">
        <el-date-picker
          v-model="selectedMonth"
          type="month"
          placeholder="选择月份"
          format="yyyy-MM"
          value-format="yyyy-MM"
          :clearable="false"
          style="width: 140px; margin-right: 12px;"
        />
        <el-button type="primary" @click="handleRefresh" :loading="loading">
          <i class="el-icon-refresh" style="margin-right: 4px;"></i>
          刷新
        </el-button>
        <el-select
          v-model="selectedTeam"
          placeholder="选择团队"
          clearable
          style="width: 160px; margin-left: 12px;"
          @change="handleTeamFilterChange"
        >
          <el-option
            label="全部团队"
            value=""
          />
          <el-option
            v-for="team in teamOptions"
            :key="team.value"
            :label="team.label"
            :value="team.value"
          />
        </el-select>
        <el-button
          type="success"
          @click="exportToExcel"
          :loading="exporting || loading"
          :disabled="!tableRows || tableRows.length === 0"
          style="margin-left: 12px;"
        >
          <i class="el-icon-download" style="margin-right: 4px;"></i>
          导出Excel
        </el-button>

        <el-button
          type="success"
          @click="syncXiaomanRecentData"
          :loading="loading"
          style="margin-left: 12px;"
        >
          同步小满数据
        </el-button>
      </div>
    </div>

    <div class="table-container">
      <div class="performance-table-container">
        <!-- 数据信息浮层 -->
        <div class="data-overlay">
          <div class="info-item">
            <span class="label">数据截止：</span>
            <span class="value">{{ dataDeadline }}</span>
          </div>
          <div class="info-item">
            <span class="label">月度时间进度：</span>
            <span class="value">{{ monthProgress }}%</span>
          </div>
          <div class="info-item">
            <span class="label">季度时间进度：</span>
            <span class="value">{{ quarterProgress }}%</span>
          </div>
        </div>

        <div class="page-title">
          <h2>{{ dynamicTitle }}</h2>
        </div>

        <div class="page-header">
        </div>

        <!-- Element UI 表格 -->
        <div class="el-table-wrapper">
          <el-table
            :data="tableRows"
            v-loading="loading"
            border
            height="100%"
            :row-class-name="getRowClassName"
            :cell-class-name="getCellClassName"
            style="width: 100%"
            :span-method="spanMethod"
          >
          <!-- 固定列 -->
          <el-table-column
            prop="teamName"
            label="团队名称"
            width="120"
            fixed="left"
            align="center"
            class-name="basic-info"
          >
            <template slot-scope="scope">
              <span :style="{ color: scope.row.teamColor || '#000000', fontWeight: scope.row.personType === 'team_leader' ? 'bold' : 'normal' }">
                {{ scope.row.teamName || '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            prop="teamLeader"
            label="队长"
            width="120"
            fixed="left"
            align="center"
            class-name="basic-info"
          >
            <template slot-scope="scope">
              {{ scope.row.teamLeader || '-' }}
            </template>
          </el-table-column>

          <el-table-column
            prop="groupLeader"
            label="组长"
            width="100"
            fixed="left"
            align="center"
            class-name="basic-info"
          >
            <template slot-scope="scope">
              <span
                :class="{
                  'group-leader-style': scope.row.personType === 'group_leader',
                  'team-leader-style': scope.row.personType === 'team_leader',
                  'no-special-style': !scope.row.groupLeader || scope.row.groupLeader === '-'
                }"
                :style="getGroupLeaderCellStyle(scope.row)"
              >
                {{ scope.row.groupLeader || '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            prop="teamMembers"
            label="组员"
            width="120"
            fixed="left"
            align="center"
            class-name="basic-info"
          >
            <template slot-scope="scope">
              <el-tooltip
                :content="scope.row.teamMembers || ''"
                placement="top"
                :disabled="!scope.row.teamMembers"
                :open-delay="500"
              >
                <span
                  :class="{
                    'group-leader-style': scope.row.personType === 'group_leader',
                    'team-leader-style': scope.row.personType === 'team_leader',
                    'no-special-style': !scope.row.teamMembers || scope.row.teamMembers === '-'
                  }"
                  :style="getTeamMembersCellStyle(scope.row)"
                >
                  {{ scope.row.teamMembers || '-' }}
                </span>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column
            prop="merchandiser"
            label="跟单员"
            width="100"
            align="center"
            class-name="basic-info"
          >
            <template slot-scope="scope">
              {{ scope.row.merchandiser || '-' }}
            </template>
          </el-table-column>

          <el-table-column
            prop="month"
            label="月份"
            width="80"
            align="center"
            class-name="basic-info"
            header-class-name="basic-info"
          >
            <template slot-scope="scope">
              {{ formatMonth(scope.row.month) }}
            </template>
          </el-table-column>

          <!-- 团队业绩列组 -->
          <el-table-column label="团队业绩" align="center" class-name="team-performance">
            <el-table-column
              prop="teamOrderTarget"
              label="团队目标"
              width="120"
              align="center"
              class-name="team-performance"
            >
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.teamOrderTarget) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="teamOrderActual"
              label="团队下单"
              width="120"
              align="center"
              class-name="team-performance"
            >
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.teamOrderActual) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="teamAchievementRate"
              label="队达成率"
              width="100"
              align="center"
              class-name="team-performance"
            >
              <template slot-scope="scope">
                {{ formatPercentage(scope.row.teamAchievementRate) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 小组业绩列组 -->
          <el-table-column label="小组业绩" align="center" class-name="group-performance">
            <el-table-column
              prop="groupOrderTarget"
              label="小组目标"
              width="120"
              align="center"
              class-name="group-performance"
            >
              <template slot-scope="scope">
                <span v-if="scope.row.personType === 'team_leader'">-</span>
                <span v-else>{{ formatCurrency(scope.row.groupOrderTarget) }}</span>
              </template>
            </el-table-column>

            <el-table-column
              prop="groupOrderActual"
              label="小组下单"
              width="120"
              align="center"
              class-name="group-performance"
            >
              <template slot-scope="scope">
                <span v-if="scope.row.personType === 'team_leader'">-</span>
                <span v-else>{{ formatCurrency(scope.row.groupOrderActual) }}</span>
              </template>
            </el-table-column>

            <el-table-column
              prop="groupAchievementRate"
              label="组达成率"
              width="100"
              align="center"
              class-name="group-performance"
            >
              <template slot-scope="scope">
                <span v-if="scope.row.personType === 'team_leader'">-</span>
                <span v-else>{{ formatPercentage(scope.row.groupAchievementRate) }}</span>
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 个人业绩列组 -->
          <el-table-column label="个人业绩" align="center" class-name="core-performance">
            <el-table-column
              prop="monthOrderTarget"
              label="月下单目标"
              width="120"
              align="center"
              class-name="core-performance"
            >
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.monthOrderTarget) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="monthOrderActual"
              label="月下单达成"
              width="120"
              align="center"
              class-name="core-performance"
            >
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.monthOrderActual) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="monthAchievementRate"
              label="月达成率"
              width="100"
              align="center"
              class-name="core-performance"
            >
              <template slot-scope="scope">
                {{ formatPercentage(scope.row.monthAchievementRate) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="ranking"
              label="排名"
              width="80"
              align="center"
              class-name="core-performance"
            >
              <template slot-scope="scope">
                {{ scope.row.ranking || '' }}
              </template>
            </el-table-column>

            <el-table-column
              prop="monthGapTarget"
              label="月缺口目标"
              width="120"
              align="center"
              class-name="gap-target"
            >
              <template slot-scope="scope">
                {{ formatGapTarget(scope.row.monthGapTarget) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 客户分析列组 -->
          <el-table-column label="客户分析" align="center" class-name="customer-analysis">
            <el-table-column
              prop="newCustomerAchievement"
              label="新客户达成"
              width="120"
              align="center"
              class-name="customer-analysis"
            >
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.newCustomerAchievement) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="newCustomerRatio"
              label="新客户占比"
              width="120"
              align="center"
              class-name="customer-analysis"
            >
              <template slot-scope="scope">
                {{ formatPercentage(scope.row.newCustomerRatio) }}
              </template>
            </el-table-column>

            <el-table-column
              prop="oldCustomerRatio"
              label="老客户占比"
              width="120"
              align="center"
              class-name="customer-analysis"
            >
              <template slot-scope="scope">
                {{ formatPercentage(scope.row.oldCustomerRatio) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 周度统计列组 -->
          <el-table-column
            v-for="(weekColumn, index) in weekColumns"
            :key="weekColumn.key"
            :label="weekColumn.title"
            align="center"
            :class-name="`weekly-group-${(index % 3) + 1}`"
          >
            <el-table-column
              :prop="`week_${index}_target`"
              label="目标"
              width="100"
              align="center"
              :class-name="`weekly-group-${(index % 3) + 1}`"
            >
              <template slot-scope="scope">
                {{ formatWeeklyValue(scope.row, getWeekIndex(weekColumn.key), 'target', false, true) }}
              </template>
            </el-table-column>

            <el-table-column
              :prop="`week_${index}_actual`"
              label="达成"
              width="100"
              align="center"
              :class-name="`weekly-group-${(index % 3) + 1}`"
            >
              <template slot-scope="scope">
                {{ formatWeeklyValue(scope.row, getWeekIndex(weekColumn.key), 'actual', false, true) }}
              </template>
            </el-table-column>

            <el-table-column
              :prop="`week_${index}_rate`"
              label="达成率"
              width="100"
              align="center"
              :class-name="`weekly-group-${(index % 3) + 1}`"
            >
              <template slot-scope="scope">
                {{ formatWeeklyValue(scope.row, getWeekIndex(weekColumn.key), 'achievementRate', true, false) }}
              </template>
            </el-table-column>

            <el-table-column
              :prop="`week_${index}_ranking`"
              label="排名"
              width="80"
              align="center"
              :class-name="`weekly-group-${(index % 3) + 1}`"
            >
              <template slot-scope="scope">
                {{ formatWeeklyValue(scope.row, getWeekIndex(weekColumn.key), 'ranking') }}
              </template>
            </el-table-column>
          </el-table-column>
        </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { request } from 'beans-admin-plugin'
import moment from 'moment'
import { PerformanceCalculationUtils } from './calculation-utils'
import * as XLSX from 'xlsx'

@Component({
  watch: {
    tableRows: {
      handler() {
        this.$nextTick(() => {
          // Element UI 表格自动处理高度，不需要手动同步
        })
      },
      deep: true
    }
  }
})
export default class WeekElPerformanceTarget extends Vue {
  // 数据状态
  loading = false
  exporting = false
  rawData = []
  tableRows = []
  allTableRows = []
  weekColumns = []
  queryDate = moment().format('YYYY-MM-DD')

  // 日期选择器状态
  selectedMonth = moment().format('YYYY-MM')
  selectedTeam = ''

  // 计算属性

  get dataDeadline() {
    return moment().format('YYYY-MM-DD')
  }

  get monthProgress() {
    return PerformanceCalculationUtils.getMonthProgress()
  }

  get quarterProgress() {
    return PerformanceCalculationUtils.getQuarterProgress()
  }

  get currentMonthDisplay() {
    return moment(this.queryDate).format('YYYY-MM')
  }

  get teamOptions() {
    return PerformanceCalculationUtils.extractTeamOptions(this.allTableRows)
  }

  get dynamicTitle() {
    if (this.selectedMonth) {
      const year = moment(this.selectedMonth).format('YYYY')
      const month = moment(this.selectedMonth).format('MM')
      return `捷展团队${year}年${month}月目标进度表`
    }
    return '捷展团队目标进度表'
  }

  async mounted() {
    await this.loadData()
  }

  async loadData() {
    this.loading = true
    try {
      const result = await request.get('reports/team_performances', {
        params: {
          date: this.queryDate,
          period: 'month'
        }
      })

      this.rawData = result

      const processedData = PerformanceCalculationUtils.processWeeklyTableData(
        this.rawData,
        this.queryDate
      )

      this.allTableRows = processedData.rows
      this.weekColumns = processedData.weekColumns

      this.applyTeamFilter()
    } catch (error) {
      this.$message.error('加载数据失败，请重试')
    } finally {
      this.loading = false
    }
  }

  handleTeamFilterChange() {
    this.applyTeamFilter()
  }

  applyTeamFilter() {
    if (!this.selectedTeam || this.selectedTeam === '') {
      this.tableRows = [...this.allTableRows]
    } else {
      this.tableRows = this.allTableRows.filter(row => row.teamName === this.selectedTeam)
    }
  }

  handleRefresh() {
    if (this.selectedMonth) {
      this.queryDate = moment(this.selectedMonth).startOf('month').format('YYYY-MM-DD')
      this.loadData()
    } else {
      this.$message.warning('请选择年份和月份')
    }
  }

  async syncXiaomanRecentData() {
    await request.put('utils/sync_xiaoman_recent_data_later')
    this.$message.success('同步数据需要一点时间，请稍后刷新页面查看数据')
  }

  // Element UI 表格相关方法
  getRowClassName({ rowIndex }) {
    const row = this.tableRows[rowIndex]
    if (!row) return ''

    const classes = []
    if (row.personType === 'team_leader') {
      classes.push('team-leader-row')
    } else if (row.personType === 'group_leader') {
      classes.push('group-leader-row')
    }

    return classes.join(' ')
  }

  getCellClassName({ column }) {
    // 使用列的property来识别列类型，而不是索引
    const prop = column.property

    // 基础信息列
    const basicInfoProps = ['teamName', 'teamLeader', 'groupLeader', 'teamMembers', 'merchandiser', 'month']
    if (basicInfoProps.includes(prop)) {
      return 'basic-info'
    }

    // 团队业绩列
    const teamPerformanceProps = ['teamOrderTarget', 'teamOrderActual', 'teamAchievementRate']
    if (teamPerformanceProps.includes(prop)) {
      return 'team-performance'
    }

    // 小组业绩列
    const groupPerformanceProps = ['groupOrderTarget', 'groupOrderActual', 'groupAchievementRate']
    if (groupPerformanceProps.includes(prop)) {
      return 'group-performance'
    }

    // 个人业绩列
    const corePerformanceProps = ['monthOrderTarget', 'monthOrderActual', 'monthAchievementRate', 'ranking']
    if (corePerformanceProps.includes(prop)) {
      return 'core-performance'
    }

    // 月缺口目标特殊列
    if (prop === 'monthGapTarget') {
      return 'gap-target'
    }

    // 客户分析列
    const customerAnalysisProps = ['newCustomerAchievement', 'newCustomerRatio', 'oldCustomerRatio']
    if (customerAnalysisProps.includes(prop)) {
      return 'customer-analysis'
    }

    // 周度统计列（动态生成的列）
    if (prop && prop.startsWith('week_')) {
      const matches = prop.match(/week_(\d+)_/)
      if (matches) {
        const weekIndex = parseInt(matches[1])
        const groupNumber = (weekIndex % 3) + 1
        return `weekly-group-${groupNumber}`
      }
    }

    return ''
  }

  // 合并单元格方法
  spanMethod({ column, rowIndex }) {
    const currentRow = this.tableRows[rowIndex]
    if (!currentRow) return [1, 1]

    const prop = column.property

    // 基础信息组内的团队名称列合并
    if (prop === 'teamName') {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的队长列合并
    if (prop === 'teamLeader') {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的组长列合并
    if (prop === 'groupLeader') {
      if (currentRow.personType === 'team_leader' || currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的跟单员列合并
    if (prop === 'merchandiser') {
      if (currentRow.personType === 'team_leader' || currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 团队业绩相关列的合并（团队级别数据）
    const teamLevelProps = ['teamOrderTarget', 'teamOrderActual', 'teamAchievementRate']
    if (teamLevelProps.includes(prop)) {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 小组业绩相关列的合并（小组级别数据）
    const groupLevelProps = ['groupOrderTarget', 'groupOrderActual', 'groupAchievementRate']
    if (groupLevelProps.includes(prop)) {
      if (currentRow.personType === 'team_leader') {
        // 队长行，这些列不需要显示具体数值，但需要占位
        return [1, 1]
      } else if (currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    return [1, 1] // 默认不合并
  }

  // 样式相关方法
  getGroupLeaderCellStyle() {
    // 移除队长行的特殊字体颜色，统一使用黑色
    return {}
  }

  getTeamMembersCellStyle() {
    // 移除队长行的特殊字体颜色，统一使用黑色
    return {}
  }

  // 数据格式化方法
  formatMonth(monthStr) {
    return PerformanceCalculationUtils.formatMonth(monthStr)
  }

  formatPercentage(value) {
    return PerformanceCalculationUtils.formatPercentage(value)
  }

  formatNumber(value) {
    return PerformanceCalculationUtils.formatNumber(value)
  }

  formatCurrency(value) {
    return PerformanceCalculationUtils.formatCurrency(value)
  }

  formatGapTarget(value) {
    if (value <= 0) {
      return '已完成'
    }
    return PerformanceCalculationUtils.formatCurrency(value)
  }

  getWeekIndex(weekKey) {
    return parseInt(weekKey.split('_')[1])
  }

  formatWeeklyValue(row, weekIndex, prop, isPercentage = false, isCurrency = false) {
    if (!row.weeklyData || !row.weeklyData[weekIndex]) {
      return isPercentage ? '0%' : (prop === 'ranking' ? '-' : '0')
    }

    const value = row.weeklyData[weekIndex][prop]

    if (prop === 'ranking') {
      return value || '-'
    }

    if (isPercentage) {
      return this.formatPercentage(value)
    } else if (isCurrency) {
      return this.formatCurrency(value)
    } else {
      return this.formatNumber(value)
    }
  }

  // 导出Excel功能
  async exportToExcel() {
    if (!this.tableRows || this.tableRows.length === 0) {
      this.$message.warning('暂无数据可导出')
      return
    }

    this.exporting = true
    try {
      const { data: excelData, merges } = this.generateExcelDataWithMerges()

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(excelData)

      if (merges && merges.length > 0) {
        ws['!merges'] = merges
      }

      const colWidths = this.generateColumnWidths()
      ws['!cols'] = colWidths

      XLSX.utils.book_append_sheet(wb, ws, '周业绩目标进度表')

      const fileName = `周业绩目标进度表-${this.currentMonthDisplay}.xlsx`
      XLSX.writeFile(wb, fileName)

      this.$message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      this.$message.error('导出失败，请重试')
    } finally {
      this.exporting = false
    }
  }

  generateExcelDataWithMerges() {
    const data = []
    const merges = []

    const header1 = [
      '团队名称', '队长', '组长', '组员', '跟单员', '月份',
      '团队目标', '团队下单', '队达成率',
      '小组目标', '小组下单', '组达成率',
      '月下单目标', '月下单达成', '月达成率', '排名', '月缺口目标',
      '新客户达成', '新客户占比', '老客户占比'
    ]

    const header2 = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']

    let currentCol = 20
    this.weekColumns.forEach((weekCol) => {
      header1.push(weekCol.title, '', '', '')
      header2.push('目标', '达成', '达成率', '排名')

      merges.push({
        s: { r: 0, c: currentCol },
        e: { r: 0, c: currentCol + 3 }
      })

      currentCol += 4
    })

    for (let col = 0; col < 20; col++) {
      merges.push({
        s: { r: 0, c: col },
        e: { r: 1, c: col }
      })
    }

    data.push(header1)
    data.push(header2)

    this.tableRows.forEach((row, rowIndex) => {
      const dataRow = [
        row.teamName || '',
        row.teamLeader || '',
        row.groupLeader || '-',
        row.teamMembers || '-',
        row.merchandiser || '-',
        this.formatMonth(row.month),
        this.formatCurrency(row.teamOrderTarget),
        this.formatCurrency(row.teamOrderActual),
        this.formatPercentage(row.teamAchievementRate),
        row.personType === 'team_leader' ? '-' : this.formatCurrency(row.groupOrderTarget),
        row.personType === 'team_leader' ? '-' : this.formatCurrency(row.groupOrderActual),
        row.personType === 'team_leader' ? '-' : this.formatPercentage(row.groupAchievementRate),
        this.formatCurrency(row.monthOrderTarget),
        this.formatCurrency(row.monthOrderActual),
        this.formatPercentage(row.monthAchievementRate),
        row.ranking || '',
        this.formatGapTarget(row.monthGapTarget),
        this.formatCurrency(row.newCustomerAchievement),
        this.formatPercentage(row.newCustomerRatio),
        this.formatPercentage(row.oldCustomerRatio)
      ]

      this.weekColumns.forEach((weekCol) => {
        const weekIndex = this.getWeekIndex(weekCol.key)
        dataRow.push(
          this.formatWeeklyValue(row, weekIndex, 'target', false, true),
          this.formatWeeklyValue(row, weekIndex, 'actual', false, true),
          this.formatWeeklyValue(row, weekIndex, 'achievementRate', true, false),
          this.formatWeeklyValue(row, weekIndex, 'ranking')
        )
      })

      data.push(dataRow)

      // 团队级别合并：团队名称、队长、团队业绩相关列
      if (row.isFirstInTeam && row.teamRowspan > 1) {
        // 团队名称列 (c: 0)
        merges.push({
          s: { r: rowIndex + 2, c: 0 },
          e: { r: rowIndex + 2 + row.teamRowspan - 1, c: 0 }
        })

        // 队长列 (c: 1)
        merges.push({
          s: { r: rowIndex + 2, c: 1 },
          e: { r: rowIndex + 2 + row.teamRowspan - 1, c: 1 }
        })

        // 团队目标列 (c: 6)
        merges.push({
          s: { r: rowIndex + 2, c: 6 },
          e: { r: rowIndex + 2 + row.teamRowspan - 1, c: 6 }
        })

        // 团队下单列 (c: 7)
        merges.push({
          s: { r: rowIndex + 2, c: 7 },
          e: { r: rowIndex + 2 + row.teamRowspan - 1, c: 7 }
        })

        // 队达成率列 (c: 8)
        merges.push({
          s: { r: rowIndex + 2, c: 8 },
          e: { r: rowIndex + 2 + row.teamRowspan - 1, c: 8 }
        })
      }

      // 组级别合并：组长、跟单员、小组业绩相关列
      if (row.isFirstInGroup && row.groupRowspan > 1) {
        // 组长列 (c: 2)
        merges.push({
          s: { r: rowIndex + 2, c: 2 },
          e: { r: rowIndex + 2 + row.groupRowspan - 1, c: 2 }
        })

        // 跟单员列 (c: 4)
        merges.push({
          s: { r: rowIndex + 2, c: 4 },
          e: { r: rowIndex + 2 + row.groupRowspan - 1, c: 4 }
        })

        // 小组目标列 (c: 9)
        if (row.personType !== 'team_leader') {
          merges.push({
            s: { r: rowIndex + 2, c: 9 },
            e: { r: rowIndex + 2 + row.groupRowspan - 1, c: 9 }
          })

          // 小组下单列 (c: 10)
          merges.push({
            s: { r: rowIndex + 2, c: 10 },
            e: { r: rowIndex + 2 + row.groupRowspan - 1, c: 10 }
          })

          // 组达成率列 (c: 11)
          merges.push({
            s: { r: rowIndex + 2, c: 11 },
            e: { r: rowIndex + 2 + row.groupRowspan - 1, c: 11 }
          })
        }
      }
    })

    return { data, merges }
  }

  generateColumnWidths() {
    const baseWidths = [
      12, 12, 12, 15, 12, 8, // 团队名称、队长、组长、组员、跟单员、月份
      15, 15, 12, // 团队目标、团队下单、队达成率
      15, 15, 12, // 小组目标、小组下单、组达成率
      15, 15, 12, 8, 15, // 月下单目标、月下单达成、月达成率、排名、月缺口目标
      15, 12, 12 // 新客户达成、新客户占比、老客户占比
    ]
    const weekWidths = []

    this.weekColumns.forEach(() => {
      weekWidths.push(12, 12, 12, 8)
    })

    return PerformanceCalculationUtils.generateExcelColumnWidths([...baseWidths, ...weekWidths])
  }
}
</script>

<style lang="scss" scoped>
// 引入公共样式变量
@import './performance-table-styles.scss';
</style>
